import 'hammerjs';
import React from 'react';
import { round } from '../constants';

export function useZoom(props: {
  scale: number;
  setScale: (scale: number | ((prevState: number) => number)) => void;
  setElementOrigin: (
    origin:
      | { x: number; y: number }
      | ((prevState: { x: number; y: number }) => { x: number; y: number })
  ) => void;
  setScreenOrigin: (
    origin:
      | { x: number; y: number }
      | ((prevState: { x: number; y: number }) => { x: number; y: number })
  ) => void;
  viewportEl: React.MutableRefObject<HTMLDivElement>;
  containerEl: React.MutableRefObject<HTMLDivElement>;
  minScale: number;
  maxScale: number;
}) {
  const propsRef = React.useRef(props);

  const [prevScrollPosition, setPrevScrollPosition] = React.useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  const preventDefault = (e: Event) => e.preventDefault();
  const wheelPreventDefault = (e: WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
    }
  };
  //prevent zooming the page normally.
  React.useEffect(() => {
    window.addEventListener('gesturestart', preventDefault);
    window.addEventListener('gesturechange', preventDefault);
    window.addEventListener('gestureend', preventDefault);
    document.body.addEventListener('wheel', wheelPreventDefault, {
      passive: false,
    });
    return () => {
      window.removeEventListener('gesturestart', preventDefault);
      window.removeEventListener('gesturechange', preventDefault);
      window.removeEventListener('gestureend', preventDefault);
      document.body.removeEventListener('wheel', wheelPreventDefault);
    };
  }, []);

  React.useEffect(() => {
    propsRef.current = props;
  }, [props.maxScale, props.minScale, props.scale]);

  const deltaRef = React.useRef(0);

  React.useEffect(() => {
    if (!props.containerEl || !props.viewportEl) {
      return;
    }

    let destroyers = [] as (() => void)[];

    //we need this for both touch devices and non-touch devices.
    const scrollEventListener = () => {
      //this is recording the scroll position of outer container as if there were no margin on the inner container,
      //in the pdfGrid component, we need to remove the old margin, make our calculations, and then apply the new margin after.
      //we do it here because we have access to the old margin.
      setPrevScrollPosition({
        x:
          round(
            props.viewportEl.current.scrollLeft -
              props.containerEl?.current?.offsetLeft
          ) || 0,
        y:
          round(
            props.viewportEl.current.scrollTop -
              props.containerEl?.current?.offsetTop
          ) || 0,
      });
    };

    props.viewportEl.current.addEventListener('scroll', scrollEventListener);
    destroyers.push(() => {
      if (props.viewportEl.current) {
        props.viewportEl.current.removeEventListener(
          'scroll',
          scrollEventListener
        );
      }
    });

    const wheelEventListener = (e: WheelEvent) => {
      if (e.ctrlKey) {
        //this is getting the position of the mouse within the viewport(screen).
        props.setScreenOrigin({
          x: e.clientX,
          y: e.clientY,
        });

        //this is getting the position of the mouse within the containerEl.
        props.setElementOrigin({
          x:
            props.viewportEl.current.scrollLeft +
            e.clientX -
            props.containerEl.current.offsetLeft,
          y:
            props.viewportEl.current.scrollTop +
            e.clientY -
            props.containerEl.current.offsetTop,
        });

        //setting a temporary scale that updates instantly.
        props.setScale((s) => {
          const maxDelta = round(propsRef.current.maxScale - s);
          const minDelta = round(propsRef.current.minScale - s);
          deltaRef.current = Math.max(
            minDelta,
            Math.min(maxDelta, deltaRef.current - (e.deltaY * 2.5) / 1000)
          );
          const next = s + deltaRef.current;
          return next;
        });
        deltaRef.current = 0;

        e.preventDefault();
      }
    };

    document.addEventListener('wheel', wheelEventListener, {
      passive: false,
    });
    destroyers.push(() => {
      document.removeEventListener('wheel', wheelEventListener);
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === '=') {
        event.preventDefault();
        props.setScreenOrigin({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        });

        //this is getting the position of the center of the screen within the containerEl.
        props.setElementOrigin({
          x:
            props.viewportEl.current.scrollLeft +
            window.innerWidth / 2 -
            props.containerEl.current.offsetLeft,
          y:
            props.viewportEl.current.scrollTop +
            window.innerHeight / 2 -
            props.containerEl.current.offsetTop,
        });
        props.setScale((s) => Math.min(propsRef.current.maxScale, s + 0.1));
      }
      if ((event.ctrlKey || event.metaKey) && event.key === '-') {
        event.preventDefault();
        props.setScreenOrigin({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        });

        //this is getting the position of the mouse within the containerEl.
        props.setElementOrigin({
          x:
            props.viewportEl.current.scrollLeft +
            window.innerWidth / 2 -
            props.containerEl.current.offsetLeft,
          y:
            props.viewportEl.current.scrollTop +
            window.innerHeight / 2 -
            props.containerEl.current.offsetTop,
        });

        props.setScale((s) => Math.max(propsRef.current.minScale, s - 0.1));
      }
    };

    document.addEventListener('keydown', handleKeyDown, { passive: false });
    destroyers.push(() => {
      document.removeEventListener('keydown', handleKeyDown);
    });

    //############################################
    //everything below here handles touch events.
    //############################################

    const handleMultiTouchMove = (event: TouchEvent) => {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleMultiTouchMove, false);

    destroyers.push(() => {
      document.removeEventListener('touchmove', handleMultiTouchMove);
    });

    let pageX = 0,
      pageY = 0,
      screenX = 0,
      screenY = 0;
    let initialPinchDistance = 0;
    let initialScale = 1;

    const handlePinchStart = (e) => {
      if (e.touches.length > 1) {
        initialPinchDistance = Math.hypot(
          e.touches[1].pageX - e.touches[0].pageX,
          e.touches[1].pageY - e.touches[0].pageY
        );
        initialScale = propsRef.current.scale;
      } else {
        initialPinchDistance = 0;
      }
    };

    props.viewportEl.current.addEventListener('touchstart', handlePinchStart);
    destroyers.push(() => {
      props.viewportEl?.current?.removeEventListener(
        'touchstart',
        handlePinchStart
      );
    });

    const handlePinch = (e: TouchEvent) => {
      if (initialPinchDistance <= 0 || e.touches.length < 2) {
        return;
      }

      e.preventDefault();

      pageX = (e.touches[0].pageX + e.touches[1].pageX) / 2;
      pageY = (e.touches[0].pageY + e.touches[1].pageY) / 2;
      pageX +=
        props.viewportEl.current.scrollLeft -
        props.containerEl?.current?.offsetLeft;
      pageY +=
        props.viewportEl.current.scrollTop -
        props.containerEl?.current?.offsetTop;

      screenX = (e.touches[0].pageX + e.touches[1].pageX) / 2;
      screenY = (e.touches[0].pageY + e.touches[1].pageY) / 2;

      props.setElementOrigin({ x: pageX, y: pageY });
      props.setScreenOrigin({ x: screenX, y: screenY });

      const newPinchDistance = Math.hypot(
        e.touches[1].pageX - e.touches[0].pageX,
        e.touches[1].pageY - e.touches[0].pageY
      );

      const pinchDifference = newPinchDistance / initialPinchDistance;

      props.setScale((s) => {
        return Math.min(
          propsRef.current.maxScale,
          Math.max(propsRef.current.minScale, pinchDifference * initialScale)
        );
      });
    };

    document.addEventListener('touchmove', handlePinch, { passive: false });
    destroyers.push(() =>
      document.removeEventListener('touchmove', handlePinch)
    );

    const handlePinchEnd = (e: TouchEvent) => {
      initialPinchDistance = 0;
      initialScale = 1;
    };

    //
    document.addEventListener('touchend', handlePinchEnd, { passive: false });
    destroyers.push(() =>
      document.removeEventListener('touchend', handlePinchEnd)
    );

    return () => {
      destroyers.forEach((destroy) => destroy());
    };
  }, []);

  return {
    prevScrollPosition: prevScrollPosition,
  };
}
